// src/api/ocr.js
import http from "./http";
import { API_ENDPOINTS } from "./endpoints";
import { TokenService } from "./tokenService";

/**
 * OCR Extract (multipart/form-data)
 *
 * Required:
 * - file
 * - modeId
 * - numberOfPages
 * - colorMode
 * - render_width
 * - render_height
 *
 * Optional:
 * - manual_mask_rects_json
 * - spot_crop_rect_json
 * - templateName
 * - debug_return_sent_image
 */
export async function ocrExtract({
  file,
  modeId,
  preprocess,
  numberOfPages,
  pageNumbers,
  requestId,

  // Accept BOTH formats so callers don't break
  renderWidth,
  renderHeight,
  render_width,
  render_height,

  // optional
  manualMaskRects,
  spotCropRect,
  templateName,
  debugReturnSentImage,
}) {
  // Resolve width/height from either camelCase or snake_case
  const widthRaw = renderWidth ?? render_width;
  const heightRaw = renderHeight ?? render_height;

  const rw = Number(widthRaw);
  const rh = Number(heightRaw);

  if (!Number.isFinite(rw) || !Number.isFinite(rh) || rw <= 0 || rh <= 0) {
    // Throwing is OK if your UI catches and shows the message.
    // If you prefer returning {success:false}, change this behavior.
    throw new Error(
      `renderWidth/renderHeight are required and must be positive numbers. Got: ${widthRaw}x${heightRaw}`
    );
  }

  if (!(file instanceof File)) {
    throw new Error("Missing or invalid file. (Expected a File object)");
  }

  if (!modeId) {
    throw new Error("Missing modeId.");
  }

  const formData = new FormData();

  // REQUIRED
  formData.append("file", file);
  formData.append("modeId", String(modeId));
  formData.append("numberOfPages", String(numberOfPages ?? 1));
  formData.append("requestId", requestId || crypto.randomUUID());
  if (Array.isArray(pageNumbers) && pageNumbers.length > 0) {
    formData.append("page_numbers_json", JSON.stringify(pageNumbers));
  }
  formData.append("colorMode", preprocess ? "bw" : "original");

  // REQUIRED by python
  formData.append("render_width", String(Math.round(rw)));
  formData.append("render_height", String(Math.round(rh)));

  // OPTIONAL: manual masking
  if (Array.isArray(manualMaskRects) && manualMaskRects.length > 0) {
    formData.append("manual_mask_rects_json", JSON.stringify(manualMaskRects));
  }

  // OPTIONAL: spot OCR
  if (spotCropRect && typeof spotCropRect === "object") {
    formData.append("spot_crop_rect_json", JSON.stringify(spotCropRect));
  }

  // OPTIONAL: template
  if (typeof templateName === "string" && templateName.trim()) {
    formData.append("templateName", templateName.trim());
  }

  // OPTIONAL: debug
  if (debugReturnSentImage) {
    formData.append("debug_return_sent_image", "true");
  }

  // Do NOT set Content-Type manually for FormData
  const res = await http.post(API_ENDPOINTS.OCR_EXTRACT, formData);
  return res.data;
}

export async function ocrExtractStream({
  file,
  modeId,
  preprocess,
  numberOfPages,
  pageNumbers,
  requestId,
  renderWidth,
  renderHeight,
  onProgress,
  onChunk,
}) {
  if (!(file instanceof File)) throw new Error("Missing or invalid file.");
  const rw = Number(renderWidth);
  const rh = Number(renderHeight);
  if (!Number.isFinite(rw) || !Number.isFinite(rh) || rw <= 0 || rh <= 0) {
    throw new Error("renderWidth/renderHeight are required.");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("modeId", String(modeId));
  formData.append("numberOfPages", String(numberOfPages ?? 1));
  formData.append("requestId", requestId || crypto.randomUUID());
  if (Array.isArray(pageNumbers) && pageNumbers.length > 0) {
    formData.append("page_numbers_json", JSON.stringify(pageNumbers));
  }
  formData.append("colorMode", preprocess ? "bw" : "original");
  formData.append("render_width", String(Math.round(rw)));
  formData.append("render_height", String(Math.round(rh)));

  const token = TokenService.getAccessToken();
  const baseUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const response = await fetch(`${baseUrl}${API_ENDPOINTS.OCR_EXTRACT_STREAM}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(body || `OCR stream failed (${response.status})`);
  }
  if (!response.body) throw new Error("OCR stream response has no body.");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalEvent = null;

  const processBlock = (block) => {
    const data = block.split(/\r?\n/).filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart()).join("\n");
    if (!data) return;
    const event = JSON.parse(data);
    if (event.type === "progress") onProgress?.(event.message || event.data || "");
    if (event.type === "chunk") onChunk?.(event.text || event.data || "");
    if (event.type === "error") throw new Error(event.message || "OCR stream failed");
    if (event.type === "done") finalEvent = event;
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    for (const block of blocks) processBlock(block);
  }
  buffer += decoder.decode();
  if (buffer.trim()) processBlock(buffer);
  if (!finalEvent) throw new Error("OCR stream ended before completion.");
  return finalEvent;
}
/* =============================
   OCR PROCESS (JSON ONLY)
 ============================= */
export async function ocrProcess({ modeId, numberOfPages, extractionMode }) {
  const res = await http.post(API_ENDPOINTS.OCR_PROCESS, {
    modeId,
    numberOfPages,
    extractionMode,
  });
  return res.data;
}

export async function ocrCopy({
  file,
  renderWidth,
  renderHeight,
  spotCropRect,
}) {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("render_width", String(Math.round(renderWidth)));
  formData.append("render_height", String(Math.round(renderHeight)));
  formData.append("requestId", crypto.randomUUID());

  if (spotCropRect) {
    formData.append(
      "spot_crop_rect_json",
      JSON.stringify(spotCropRect)
    );
  }

  const res = await http.post(
    API_ENDPOINTS.OCR_COPY,
    formData
  );

  return res.data;
}

export async function ocrAlign({
  file,
  templateName,
  numberOfPages,
  renderWidth,
  renderHeight,
}) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("templateName", templateName);
  formData.append("numberOfPages", String(numberOfPages ?? 1));
  formData.append("render_width", String(Math.round(renderWidth)));
  formData.append("render_height", String(Math.round(renderHeight)));

  const res = await http.post(API_ENDPOINTS.OCR_ALIGN, formData);
  return res.data;
}
